import { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
    validatePasswordResetToken,
    resetPassword,
    validatePasswordResetTokenLegacy,
    resetPasswordLegacy,
} from '~/services/account';
import { useToasts } from 'react-toast-notifications';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { PasswordInput, TextInput } from '~/shared/components/form/Inputs';
import { Container, Button, Card, CardBody, Col, Row, Form } from 'reactstrap';
import { Loading } from '../../shared/components/Loading';
import { RegistrationContext } from '~/context/registration';
import { AuthContext } from '~/context/auth';

const validationSchema = Yup.object().shape({
    password: Yup.string().required('Required').min(8),
});

export const CreatePasswordLegacy = () => {
    const [{}, userDispatch] = useContext(AuthContext);
    const [loading, setLoading] = useState<boolean>(true);
    const [tokenUser, setTokenUser] = useState<any>();
    const [tokenEmail, setTokenEmail] = useState<string>('');
    const [showInvalid, setShowInvalid] = useState<boolean>(false);
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const token = params.get('token');
    let legacy_param = params.get('legacy');
    let is_legacy = true;
    if (legacy_param === '0') {
        is_legacy = false;
    }
    const is_team_member = params.get('team');
    const { addToast } = useToasts();
    const history = useHistory();
    const {
        handleSubmit,
        control,
        watch,
        formState: { isSubmitting, isValid },
    } = useForm({
        defaultValues: { email: '', password: '', password_confirmation: '' },
        mode: 'onBlur',
        resolver: yupResolver(validationSchema),
    });

    useEffect(() => {
        // Delete localStorage token if set
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }, []);

    const validateToken = async () => {
        try {
            if (token) {
                let resp;
                if (is_legacy) {
                    resp = await validatePasswordResetTokenLegacy(token);
                } else {
                    resp = await validatePasswordResetToken(token);
                }
                if (resp.detail === 'Not found.') {
                    addToast('Invalid token.', { appearance: 'error' });
                    setShowInvalid(true);
                } else {
                    setTokenUser(resp);
                }
                if (resp?.email) {
                    setTokenEmail(resp.email);
                }
                setLoading(false);
            }
        } catch (e) {
            addToast('something went wrong!', { appearance: 'error' });
        }
    };

    useEffect(() => {
        validateToken();
    }, [token]);

    const onSubmit = async (data: { password: string }) => {
        if (token) {
            try {
                let resp;
                if (is_legacy) {
                    resp = await resetPasswordLegacy(token, data.password);
                } else {
                    resp = await resetPassword(token, data.password);
                }

                console.log('Password reset response:', resp);

                // Store authentication token (legacy users always get a token)
                localStorage.setItem('token', resp.token);

                // Store user data if available
                if (resp.user) {
                    localStorage.setItem('user', JSON.stringify(resp.user));
                    
                    // Set user in AuthContext so Dashboard can fetch campaign
                    userDispatch({
                        type: 'set_user',
                        user: resp.user,
                    });
                }

                // Legacy users should always go to dashboard after password creation
                addToast('Password set successfully!', {
                    appearance: 'success',
                });
                history.push('/dashboard');
            } catch (error) {
                console.error('Password reset failed:', error);
                addToast('Failed to set password', { appearance: 'error' });
            }
        }
    };

    const InvalidToken = () => {
        return (
            <div className='py-5 text-center'>
                <h3 className='mb-5'>Invalid Token</h3>
                <p>
                    This token is either invalid or has expired. Try resetting
                    your password from the login page.
                </p>
                <Button color='primary' onClick={() => history.push('/')}>
                    Return to Login
                </Button>
            </div>
        );
    };

    if (loading) {
        return <Loading />;
    }

    if (showInvalid) {
        return (
            <Container>
                <InvalidToken />
            </Container>
        );
    }

    return (
        <Container>
            <Row className='justify-content-center py-5'>
                <Col md='6'>
                    <div>
                        <div className='text-center'>
                            <h3 className='mb-5'>
                                Welcome back, {tokenUser.first_name}
                            </h3>
                            <p>
                                Finish creating your Help Hope Live profile by
                                creating a strong password. We recommend saving
                                this password to your browser or a password
                                manager application.
                            </p>
                        </div>

                        <Card
                            className='box-shadow my-5'
                            style={{
                                maxWidth: '400px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                            }}
                        >
                            <CardBody>
                                <Form
                                    onSubmit={handleSubmit(onSubmit)}
                                    key='password-form'
                                >
                                    <TextInput
                                        name='email'
                                        label='Confirm Your Email'
                                        type='email'
                                        control={control}
                                        required={true}
                                        ariaRequired={true}
                                        maxLength={255}
                                        newRules={{
                                            validate: (value: string) => {
                                                if (!tokenEmail) {
                                                    return true;
                                                }
                                                const normalized = value
                                                    ?.trim()
                                                    .toLowerCase();
                                                return (
                                                    normalized ===
                                                        tokenEmail
                                                            .trim()
                                                            .toLowerCase() ||
                                                    'Email must match the address on your reset link'
                                                );
                                            },
                                        }}
                                    />
                                    <PasswordInput
                                        name='password'
                                        label='Create Password'
                                        control={control}
                                        required={true}
                                        ariaRequired={true}
                                        helperText='Must contain at least 8 characters'
                                    />
                                    <PasswordInput
                                        name='password_confirmation'
                                        label='Confirm Password'
                                        control={control}
                                        required={true}
                                        ariaRequired={true}
                                        validate={(value: string) => {
                                            const password = watch('password');
                                            return (
                                                value === password ||
                                                'Passwords must match'
                                            );
                                        }}
                                    />
                                    <br />
                                    <Button
                                        color='primary'
                                        type='submit'
                                        disabled={isSubmitting || !isValid}
                                    >
                                        {isSubmitting
                                            ? `Saving`
                                            : `Save Password`}
                                    </Button>
                                </Form>
                            </CardBody>
                        </Card>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};
